import React from "react";

//material ui
import { useTheme } from "@mui/styles";
import { Box, Typography, useMediaQuery } from "@mui/material";

//img
import CourseModalityBgDesktop from "../../img/CourseModalityBgDesktop.png";

//json2mq
import json2mq from "json2mq";
import useTranslation from "../../hooks/useTranslation";
import { useLocation } from "react-router-dom";

const useStyles = (theme) => ({
  container: {
    width: "100vw",
    backgroundImage: `url(${CourseModalityBgDesktop})`,
    backgroundSize: "96%",
    display: "flex",
    justifyContent: "space-between",
  },
  line: {
    width: "25px",
    border: `1px solid ${theme.palette.red.main}`,
    marginRight: "8px",
    height: 0,
    backgroundColor: theme.palette.red.main,
  },
  titleContainer: {
    display: { md: "flex" },
    alignItems: { md: "center" },
  },
  title: {
    color: theme.palette.red.main,
    fontSize: { md: "14px", lg: "16px" },
    letterSpacing: "1.6px",
    fontFamily: theme.typography.montserrat.semibold,
  },
  firstChild: {
    display: "flex",
    flexDirection: "column",
    ml: { md: "100px", lg: "150px", xl: "200px" },
  },
  subtitle: {
    color: theme.palette.blue.main,
    lineHeight: { md: "40px", lg: "50px", xl: "65px" },
    fontFamily: theme.typography.montserrat.light,
    maxWidth: { md: "500px", lg: "600px", xl: "745px" },
    height: { md: "73.25px", lg: "74.5px", xl: "79px" },
    mt: { md: "22px", lg: "26.5px", xl: "31px" },
  },
  span: {
    fontWeight: "bold",
  },
  box: {
    display: "flex",
    mt: { md: "30px", lg: "59px" },
  },
  mainText: {
    fontSize: { md: "20px", lg: "25px", xl: "30px" },
    fontFamily: theme.typography.montserrat.bold,
    lineHeight: { md: "25px", lg: "30px", xl: "36px" },
    color: theme.palette.blue.main,
    mb: "12px",
  },
  secondaryText: {
    fontSize: { md: "18px", lg: "30px", xl: "34px" },
    fontFamily: theme.typography.anaHeim.regular,
    lineHeight: { md: "30px", lg: "35px", xl: "48px" },
    color: theme.palette.blue.main,
  },
  textContainer: { display: "flex", flexDirection: "column", ml: "29.1px" },
});

const CourseModalityCardsDesktop = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMediaQuery("(max-width:899px)");

  const stylesHelper = () => {
    if (xl) {
      return {
        img: { width: "743px", height: "817px" },
      };
    } else if (md) {
      return {
        img: { width: "500px", height: "500px" },
      };
    } else if (lg) {
      return {
        img: { width: "650px", height: "700px" },
      };
    } else if (xxl) {
      return {
        img: { width: "800px", height: "817px" },
      };
    }
  };

  const md = useMediaQuery(
    json2mq({
      maxWidth: 1200,
      minWidth: 900,
    })
  );

  const lg = useMediaQuery(
    json2mq({
      maxWidth: 1535,
      minWidth: 1201,
    })
  );

  const xl = useMediaQuery(
    json2mq({
      minWidth: 1536,
      maxWidth: 1800,
    })
  );

  const xxl = useMediaQuery(
    json2mq({
      minWidth: 1801,
    })
  );

  const { pathname } = useLocation();
  const componentName = "CourseModalityCardsDesktop";
  const translation = useTranslation(pathname, componentName);
  const { backgroundImg, title, subtitle, cards, rightSideImg } = translation;
  
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        mt: { md: "107.5px" },
        alignItems: "flex-end",
      }}
    >
      {!isMobile && backgroundImg && (
        <img
          src={backgroundImg.src}
          alt={backgroundImg.alt}
          style={{
            width: "174.6px",
            height: "173.4px",
            marginBottom: "31.1px",
          }}
        />
      )}
      <Box sx={classes.container}>
        <Box sx={classes.firstChild}>
          <Box sx={classes.titleContainer}>
            <div style={classes.line}></div>
            <Typography sx={classes.title}>{title}</Typography>
          </Box>

          <Typography
            sx={{
              ...classes.subtitle,
              fontSize: xxl ? "70px" : { md: "30px", lg: "40px", xl: "56px" },
              maxWidth: xxl
                ? "900px"
                : { md: "500px", lg: "600px", xl: "745px" },
            }}
          >
            {subtitle[0]}
            <span style={classes.span}>{subtitle[1]}</span>
          </Typography>

          <Box sx={classes.box}>
            {cards[0].img && (
              <Box>
                <img
                  src={cards[0].img.src}
                  alt={cards[0].img.alt}
                  style={{ height: "56px", width: "56px" }}
                />
              </Box>
            )}
            <Box sx={classes.textContainer}>
              <Typography sx={classes.mainText}>{cards[0].title}</Typography>
              <Typography sx={classes.secondaryText}>
                {cards[0].subtitle[0]}
              </Typography>
              <Typography sx={classes.secondaryText}>
                {cards[0].subtitle[1]}
              </Typography>
            </Box>
          </Box>

          <Box sx={classes.box}>
            {cards[1].img && (
              <Box>
                <img
                  src={cards[1].img.src}
                  alt={cards[1].img.alt}
                  style={{ height: "56px", width: "56px" }}
                />
              </Box>
            )}
            <Box sx={classes.textContainer}>
              <Typography sx={classes.mainText}>{cards[1].title}</Typography>
              <Typography sx={classes.secondaryText}>
                {cards[1].subtitle[0]}
              </Typography>
              <Typography sx={classes.secondaryText}>
                {cards[1].subtitle[1]}
              </Typography>
            </Box>
          </Box>

          <Box sx={classes.box}>
            {cards[2].img && (
              <Box>
                <img
                  src={cards[2].img.src}
                  alt={cards[2].img.alt}
                  style={{ height: "56px", width: "56px" }}
                />
              </Box>
            )}
            <Box sx={classes.textContainer}>
              <Typography sx={classes.mainText}>{cards[2].title}</Typography>
              <Typography sx={classes.secondaryText}>
                {cards[2].subtitle[0]} <br /> {cards[2].subtitle[1]}
              </Typography>
            </Box>
          </Box>
        </Box>

        {rightSideImg && (
          <Box sx={classes.imgContainer}>
            <img
              src={rightSideImg.src}
              alt={rightSideImg.alt}
              style={stylesHelper() && stylesHelper().img}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CourseModalityCardsDesktop;
